import React, { createContext, useState, useEffect } from "react";
import {
  clearSession,
  isTokenExpired,
  onAuthEvent,
  refreshAccessToken,
} from "../../services/authSession";

const AuthContext = createContext();

// "Keep me signed in on this device" unticked: the session lasts only until
// the browser closes. sessionStorage survives reloads but not a restart.
const SESSION_ONLY_KEY = "session_only";
const SESSION_ALIVE_KEY = "session_alive";

export const AuthProvider = ({ children }) => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [user, setUser] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [authTokens, setAuthTokens] = useState(null);
  // True until auth state has been hydrated from localStorage, so route guards
  // don't bounce a logged-in user to /signin on the first render after refresh.
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    try {
      if (
        localStorage.getItem(SESSION_ONLY_KEY) &&
        !sessionStorage.getItem(SESSION_ALIVE_KEY)
      ) {
        clearSession();
        localStorage.removeItem(SESSION_ONLY_KEY);
      }
      const storedUser = localStorage.getItem("user");
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");

      // A session whose refresh token has run out can't be renewed: sign out
      // now rather than showing the user as signed in while every save fails.
      if (accessToken && refreshToken && isTokenExpired(refreshToken)) {
        clearSession();
        setIsSignedIn(false);
        setAuthTokens(null);
      } else if (accessToken) {
        setIsSignedIn(true);
        setAuthTokens({ access: accessToken, refresh: refreshToken });
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            // Corrupt stored user — drop it rather than break init.
            localStorage.removeItem("user");
          }
        }
      } else {
        setIsSignedIn(false);
        setAuthTokens(null);
      }
    } finally {
      // Always resolve init so route guards / landing never hang on a blank page.
      setIsInitializing(false);
    }

    // An expired access token is renewed up front, so the first request of
    // the visit doesn't have to fail first (the axios interceptor in
    // services/authSession.js covers tokens that expire later on).
    const accessToken = localStorage.getItem("access_token");
    if (accessToken && isTokenExpired(accessToken) && localStorage.getItem("refresh_token")) {
      refreshAccessToken();
    }
  }, []);

  // Keep context state in step with renewals and expiry from anywhere.
  useEffect(() => {
    const offRefreshed = onAuthEvent("refreshed", (tokens) => setAuthTokens(tokens));
    const offExpired = onAuthEvent("expired", () => {
      setUser(null);
      setAuthTokens(null);
      setIsSignedIn(false);
    });
    return () => {
      offRefreshed();
      offExpired();
    };
  }, []);

  const fetchFullUser = async (token) => {
    try {
      const response = await fetch(`${SERVER_URL}/auth/users/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const fullUser = await response.json();
        localStorage.setItem("user", JSON.stringify(fullUser));
        setUser(fullUser);
      } else {
        console.error("Failed to fetch user details:", response.status);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const login = (userData, accessToken, refreshToken, { remember = true } = {}) => {
    console.log("AuthContext - Logging in with userData:", userData);
    const tokens = { access: accessToken, refresh: refreshToken };
    if (remember) {
      localStorage.removeItem(SESSION_ONLY_KEY);
    } else {
      localStorage.setItem(SESSION_ONLY_KEY, "1");
      sessionStorage.setItem(SESSION_ALIVE_KEY, "1");
    }
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    setUser(userData);
    setAuthTokens(tokens);
    setIsSignedIn(true);

    // If first_name is missing, fetch it
    if (!userData.first_name) {
      fetchFullUser(accessToken);
    }
  };

  // Merge fresh fields into the signed-in user (e.g. after a password change).
  const updateUser = (changes) => {
    setUser((current) => {
      const next = { ...(current || {}), ...changes };
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    clearSession();
    localStorage.removeItem(SESSION_ONLY_KEY);
    setUser(null);
    setAuthTokens(null);
    setIsSignedIn(false);
  };

  // Role drives Admin Mode (clinician) vs Patient Mode (patient) in the UI.
  const role = user?.role ?? null;
  const isClinician = role === "clinician";
  const isPatient = role === "patient";

  return (
    <AuthContext.Provider
      value={{
        user,
        isSignedIn,
        authTokens,
        login,
        logout,
        updateUser,
        role,
        isClinician,
        isPatient,
        isInitializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
